/**
 * userResourcesStore — single source of truth for the current user's
 * registered resources and the selected-resource coupling.
 *
 * WHY THIS EXISTS (Rails Phase 2 / DEV-1375): the registered-resources
 * registry moved out of ResourcesPage into an App-mounted Rail
 * (ResourceRegistryRail), while the needs/matching column stays on the page.
 * Both surfaces need the same `resources` array AND the same
 * `selectedResourceGuid` (left selection re-ranks the right needs). Page
 * state can't span an App-level sibling and a route, so the coupling is
 * lifted here. ONE copy of the selection — duplicating it would silently
 * break the select→match link (the documented risk).
 *
 * Pure module store (no React) following the notificationStore idiom; the
 * React binding lives in src/hooks/useUserResources.ts via
 * useSyncExternalStore.
 */
import { fetchUserResources } from '../../api/userResources';
import type { UserResource } from './userResourceTypes';

export interface UserResourcesState {
  resources: UserResource[];
  selectedResourceGuid: string | null;
  loading: boolean;
  error: string | null;
  /** The userGuid the current `resources` were loaded for (dedup guard). */
  loadedUserGuid: string | null;
}

let state: UserResourcesState = {
  resources: [],
  selectedResourceGuid: null,
  loading: false,
  error: null,
  loadedUserGuid: null,
};

let listeners: Array<() => void> = [];
/** Guards against concurrent loads for the same user. */
let inFlightFor: string | null = null;

function emit(): void {
  listeners.forEach((fn) => fn());
}

/** Replace state immutably so useSyncExternalStore sees a new snapshot. */
function setState(patch: Partial<UserResourcesState>): void {
  state = { ...state, ...patch };
  emit();
}

/* ── React integration ── */
export function subscribe(listener: () => void): () => void {
  listeners.push(listener);
  return () => {
    listeners = listeners.filter((l) => l !== listener);
  };
}

export function getSnapshot(): UserResourcesState {
  return state;
}

/* ── Actions ── */

/**
 * Load the user's resources. Deduplicated: a no-op if already loaded for the
 * same user (unless `force`), and concurrent calls for the same user collapse
 * to one fetch. Off-section callers pass an empty userGuid → no-op, so the
 * App-wide Rail mount doesn't fetch on every route.
 */
export async function loadUserResources(
  userGuid: string,
  opts?: { force?: boolean },
): Promise<void> {
  const u = (userGuid || '').trim();
  if (!u) return;
  if (!opts?.force && state.loadedUserGuid === u) return;
  if (inFlightFor === u) return;

  inFlightFor = u;
  setState({ loading: true, error: null });
  try {
    const data = await fetchUserResources(u);
    state = { ...state, resources: data, loading: false, error: null, loadedUserGuid: u };
    emit();
  } catch (e) {
    state = {
      ...state,
      loading: false,
      error: e instanceof Error ? e.message : 'Failed to load resources',
    };
    emit();
  } finally {
    if (inFlightFor === u) inFlightFor = null;
  }
}

/** Set resources directly or via updater (mirrors React setState shape). */
export function setUserResources(
  updater: UserResource[] | ((prev: UserResource[]) => UserResource[]),
): void {
  const next = typeof updater === 'function' ? updater(state.resources) : updater;
  setState({ resources: next });
}

/** Set the selected resource (re-ranks needs). No-op if unchanged. */
export function setSelectedResourceGuid(guid: string | null): void {
  if (state.selectedResourceGuid === guid) return;
  setState({ selectedResourceGuid: guid });
}

export function setUserResourcesError(error: string | null): void {
  setState({ error });
}
